/**
 * Clients Registry Hooks (v2)
 *
 * Unified merge-and-classify pattern:
 * 1. Fetch all org clients with contacts (base dataset)
 * 2. Fetch aircraft counts per client (ownership enrichment)
 * 3. Fetch active lease data per client (relationship enrichment)
 * 4. Fetch transient stay data per client (relationship enrichment)
 * 5. Merge into unified ClientRegistryRow[] with computed relationship/crm_stage/anomalies
 * 6. Apply quick filters client-side
 */

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTenant } from '@/contexts'
import { supabase } from '@/lib/supabase'
import { applyRecordStatusFilter } from '@/lib/api/helpers/apply-record-status-filter'
import type { RecordStatusFilter } from '@/types/record-status'
import type { ClientRegistryRow, ClientRegistrySummary, ClientSavedViewId, ClientQuickFilterState, ClientRelationshipFilter } from '@/types/clients'
import { mapClientStatusToCrmStage, computeClientAnomalyFlags } from '@/types/clients'

// ============================================
// Query Keys
// ============================================

export const clientRegistryKeys = {
  all: ['client-registry'] as const,
  clients: (organizationId: string) =>
    [...clientRegistryKeys.all, 'clients', organizationId] as const,
  aircraft: (organizationId: string) =>
    [...clientRegistryKeys.all, 'aircraft', organizationId] as const,
  leases: (organizationId: string) =>
    [...clientRegistryKeys.all, 'leases', organizationId] as const,
  transients: (organizationId: string) =>
    [...clientRegistryKeys.all, 'transients', organizationId] as const,
}

// ============================================
// Sub-query 1: All Org Clients with Contacts
// ============================================

function useAllOrgClients(recordStatus: RecordStatusFilter = 'active') {
  const { currentOrganization } = useTenant()
  const organizationId = currentOrganization?.id

  return useQuery({
    queryKey: [...clientRegistryKeys.clients(organizationId || ''), recordStatus],
    queryFn: async () => {
      let query = supabase
        .from('clients')
        .select(`
          *,
          contacts (
            first_name,
            last_name,
            email_primary,
            phone_mobile,
            is_primary_contact
          )
        `)
        .eq('organization_id', organizationId!)
        .order('created_at', { ascending: false })

      query = applyRecordStatusFilter(query, recordStatus)

      const { data, error } = await query

      if (error) throw error
      return data ?? []
    },
    enabled: !!organizationId,
    refetchOnWindowFocus: true,
  })
}

// ============================================
// Sub-query 2: Aircraft Counts per Client
// ============================================

interface ClientAircraftInfo {
  count: number
  tailNumbers: string[]
}

function useClientAircraftCounts() {
  const { currentOrganization } = useTenant()
  const organizationId = currentOrganization?.id

  return useQuery({
    queryKey: clientRegistryKeys.aircraft(organizationId || ''),
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<Map<string, ClientAircraftInfo>> => {
      const { data, error } = await supabase
        .from('aircraft')
        .select('id, tail_number, owner_client_id, operator_client_id')
        .eq('organization_id', organizationId!)
        .is('deleted_at', null)

      if (error) throw error

      const map = new Map<string, ClientAircraftInfo>()

      for (const aircraft of data ?? []) {
        // Collect unique client IDs for this aircraft (deduplicate owner=operator)
        const clientIds = new Set<string>()
        if (aircraft.owner_client_id) clientIds.add(aircraft.owner_client_id)
        if (aircraft.operator_client_id) clientIds.add(aircraft.operator_client_id)

        for (const clientId of clientIds) {
          const existing = map.get(clientId)
          if (existing) {
            existing.count++
            existing.tailNumbers.push(aircraft.tail_number)
          } else {
            map.set(clientId, {
              count: 1,
              tailNumbers: [aircraft.tail_number],
            })
          }
        }
      }

      return map
    },
    enabled: !!organizationId,
  })
}

// ============================================
// Sub-query 3: Active Lease Data per Client
// ============================================

interface ClientLeaseInfo {
  count: number
  monthlyTotal: number
  nearestEndDate: string | null
  locationNames: string[]
}

function useClientLeaseData() {
  const { currentOrganization } = useTenant()
  const organizationId = currentOrganization?.id

  return useQuery({
    queryKey: clientRegistryKeys.leases(organizationId || ''),
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<Map<string, ClientLeaseInfo>> => {
      const { data, error } = await supabase
        .from('lease_contracts')
        .select(`
          id,
          client_id,
          monthly_rate,
          status,
          end_date,
          location:location_id (
            name
          )
        `)
        .eq('organization_id', organizationId!)
        .in('status', ['active', 'expiring'])
        .is('deleted_at', null)

      if (error) throw error

      const map = new Map<string, ClientLeaseInfo>()

      for (const lease of data ?? []) {
        const location = lease.location as unknown as { name: string } | null
        const locationName = location?.name ?? null

        const existing = map.get(lease.client_id)
        if (existing) {
          existing.count++
          existing.monthlyTotal += Number(lease.monthly_rate) || 0
          if (locationName && !existing.locationNames.includes(locationName)) {
            existing.locationNames.push(locationName)
          }
          // Track nearest end date
          if (lease.end_date) {
            if (!existing.nearestEndDate || lease.end_date < existing.nearestEndDate) {
              existing.nearestEndDate = lease.end_date
            }
          }
        } else {
          map.set(lease.client_id, {
            count: 1,
            monthlyTotal: Number(lease.monthly_rate) || 0,
            nearestEndDate: lease.end_date ?? null,
            locationNames: locationName ? [locationName] : [],
          })
        }
      }

      return map
    },
    enabled: !!organizationId,
  })
}

// ============================================
// Sub-query 4: Transient Stay Data per Client
// ============================================

interface ClientTransientInfo {
  activeCount: number
  visits12mo: number
  locationNames: string[]
}

function useClientTransientData() {
  const { currentOrganization } = useTenant()
  const organizationId = currentOrganization?.id

  return useQuery({
    queryKey: clientRegistryKeys.transients(organizationId || ''),
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<Map<string, ClientTransientInfo>> => {
      const { data, error } = await supabase
        .from('transient_stays')
        .select(`
          id, client_id, status, check_in,
          location:location_id (
            name
          )
        `)
        .eq('organization_id', organizationId!)
        .is('deleted_at', null)

      if (error) throw error

      const now = new Date()
      const twelveMonthsAgo = new Date(now)
      twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12)

      const map = new Map<string, ClientTransientInfo>()
      const activeStatuses = ['reserved', 'confirmed', 'checked_in']

      for (const stay of data ?? []) {
        const isActive = activeStatuses.includes(stay.status)
        const isWithin12mo = stay.check_in && new Date(stay.check_in) >= twelveMonthsAgo
        const location = stay.location as unknown as { name: string } | null
        const locationName = location?.name ?? null

        const existing = map.get(stay.client_id)
        if (existing) {
          if (isActive) existing.activeCount++
          if (isWithin12mo) existing.visits12mo++
          if (locationName && !existing.locationNames.includes(locationName)) {
            existing.locationNames.push(locationName)
          }
        } else {
          map.set(stay.client_id, {
            activeCount: isActive ? 1 : 0,
            visits12mo: isWithin12mo ? 1 : 0,
            locationNames: locationName ? [locationName] : [],
          })
        }
      }

      return map
    },
    enabled: !!organizationId,
  })
}

// ============================================
// Quick Filter Application
// ============================================

function applyQuickFilters(
  data: ClientRegistryRow[],
  filters: ClientQuickFilterState
): ClientRegistryRow[] {
  return data.filter((row) => {
    // Account type filter (empty = show all)
    if (filters.accountType.length > 0) {
      if (!filters.accountType.includes(row.client_type)) return false
    }

    // Relationship filter (empty = show all)
    if (filters.relationship.length > 0) {
      if (!filters.relationship.includes(row.computed_relationship)) return false
    }

    // CRM stage filter (empty = show all)
    if (filters.crmStage.length > 0) {
      if (!filters.crmStage.includes(row.crm_stage)) return false
    }

    return true
  })
}

// ============================================
// At Risk Filter (for the At Risk view)
// ============================================

function filterAtRiskClients(data: ClientRegistryRow[]): ClientRegistryRow[] {
  return data.filter((row) => row.anomalies.length > 0)
}

// ============================================
// Unified Registry Hook
// ============================================

export function useClientsRegistry(
  activeView: ClientSavedViewId,
  quickFilters: ClientQuickFilterState,
  recordStatus: RecordStatusFilter = 'active'
) {
  const allClientsQuery = useAllOrgClients(recordStatus)
  const aircraftQuery = useClientAircraftCounts()
  const leaseQuery = useClientLeaseData()
  const transientQuery = useClientTransientData()

  // Merge all data sources into unified ClientRegistryRow[]
  const mergedData = useMemo((): ClientRegistryRow[] => {
    const allClients = allClientsQuery.data
    if (!allClients) return []

    const aircraftMap = aircraftQuery.data ?? new Map()
    const leaseMap = leaseQuery.data ?? new Map()
    const transientMap = transientQuery.data ?? new Map()

    return allClients.map((client): ClientRegistryRow => {
      // Pick primary contact
      const contacts = (client.contacts as unknown as Array<{
        first_name: string
        last_name: string
        email_primary: string
        phone_mobile: string | null
        is_primary_contact: boolean | null
      }>) ?? []

      const primaryContact = contacts.find((c) => c.is_primary_contact) ?? contacts[0] ?? null

      // Lookup enrichment data
      const aircraftInfo = aircraftMap.get(client.id)
      const leaseInfo = leaseMap.get(client.id)
      const transientInfo = transientMap.get(client.id)

      // Combine distinct location names from leases + transient stays
      const allLocationNames = new Set<string>()
      for (const name of leaseInfo?.locationNames ?? []) allLocationNames.add(name)
      for (const name of transientInfo?.locationNames ?? []) allLocationNames.add(name)
      const allLocationNamesArray = Array.from(allLocationNames)

      // Compute relationship
      const hasActiveLease = (leaseInfo?.count ?? 0) > 0
      const hasActiveTransient = (transientInfo?.activeCount ?? 0) > 0

      let computed_relationship: ClientRelationshipFilter = 'no_contract'
      if (hasActiveLease && hasActiveTransient) {
        computed_relationship = 'both'
      } else if (hasActiveLease) {
        computed_relationship = 'based_tenant'
      } else if (hasActiveTransient) {
        computed_relationship = 'transient_guest'
      }

      // Map CRM stage from client status
      const crm_stage = mapClientStatusToCrmStage(client.status)

      const row: ClientRegistryRow = {
        // Base client fields
        id: client.id,
        organization_id: client.organization_id,
        client_number: client.client_number,
        client_type: client.client_type,
        first_name: client.first_name,
        last_name: client.last_name,
        company_name: client.company_name,
        legal_entity_name: client.legal_entity_name,
        email_primary: client.email_primary,
        phone_primary: client.phone_primary,
        phone_secondary: client.phone_secondary,
        address_line1: client.address_line1,
        address_line2: client.address_line2,
        city: client.city,
        state_province: client.state_province,
        postal_code: client.postal_code,
        country: client.country,
        payment_terms: client.payment_terms,
        tax_exempt: client.tax_exempt,
        tax_id: client.tax_id,
        status: client.status,
        preferences: client.preferences as Record<string, unknown> | null,
        notes: client.notes,
        created_at: client.created_at,
        created_by: client.created_by,
        updated_at: client.updated_at,
        updated_by: client.updated_by,
        deleted_at: client.deleted_at,
        deleted_by: client.deleted_by,

        // Joined from contacts
        primary_contact_name: primaryContact
          ? [primaryContact.first_name, primaryContact.last_name].filter(Boolean).join(' ')
          : null,
        primary_contact_email: primaryContact?.email_primary ?? null,
        primary_contact_phone: primaryContact?.phone_mobile ?? null,
        contact_count: contacts.length,

        // Aircraft data
        aircraft_count: aircraftInfo?.count ?? 0,
        aircraft_tail_numbers: aircraftInfo?.tailNumbers ?? [],

        // Lease data
        active_lease_count: leaseInfo?.count ?? 0,
        active_lease_monthly_total: leaseInfo?.monthlyTotal ?? null,
        nearest_lease_end_date: leaseInfo?.nearestEndDate ?? null,
        lease_location_names: leaseInfo?.locationNames ?? [],

        // Transient data
        active_transient_count: transientInfo?.activeCount ?? 0,
        transient_visits_12mo: transientInfo?.visits12mo ?? 0,

        // Combined allocation locations
        allocation_location_count: allLocationNamesArray.length,
        allocation_location_names: allLocationNamesArray,

        // Computed
        computed_relationship,
        crm_stage,
        anomalies: [],
      }

      // Compute anomaly flags
      row.anomalies = computeClientAnomalyFlags(row)

      return row
    })
  }, [allClientsQuery.data, aircraftQuery.data, leaseQuery.data, transientQuery.data])

  // Apply view-specific filtering (At Risk view only shows flagged clients)
  const viewFilteredData = useMemo(() => {
    if (activeView === 'at_risk') {
      return filterAtRiskClients(mergedData)
    }
    return mergedData
  }, [mergedData, activeView])

  // Apply quick filters
  const filteredData = useMemo(
    () => applyQuickFilters(viewFilteredData, quickFilters),
    [viewFilteredData, quickFilters]
  )

  // Compute summary
  const summary = useMemo((): ClientRegistrySummary => {
    const data = filteredData

    return {
      totalClients: data.length,
      activeCount: data.filter((r) => r.crm_stage === 'active').length,
      leadCount: data.filter((r) => r.crm_stage === 'lead').length,
      prospectCount: data.filter((r) => r.crm_stage === 'prospect').length,
      formerCount: data.filter((r) => r.crm_stage === 'former').length,
      inactiveCount: data.filter((r) => r.crm_stage === 'inactive').length,
      basedTenantCount: data.filter((r) => r.computed_relationship === 'based_tenant').length,
      transientGuestCount: data.filter((r) => r.computed_relationship === 'transient_guest').length,
      bothCount: data.filter((r) => r.computed_relationship === 'both').length,
      noContractCount: data.filter((r) => r.computed_relationship === 'no_contract').length,
      atRiskCount: data.filter((r) => r.anomalies.length > 0).length,
      leasesExpiringCount: data.filter((r) => r.anomalies.includes('lease_expiring')).length,
      monthlyCommittedTotal: data.reduce((sum, r) => sum + (r.active_lease_monthly_total ?? 0), 0),
      arOutstanding: 0, // Hardcoded until invoicing is implemented
    }
  }, [filteredData])

  // Per-view unfiltered counts (for tab badges)
  const viewCounts = useMemo((): Record<ClientSavedViewId, number> => {
    const all = mergedData
    return {
      portfolio: all.filter((r) => r.crm_stage === 'active').length,
      leads: all.filter((r) => r.crm_stage === 'lead' || r.crm_stage === 'prospect').length,
      at_risk: all.filter((r) => r.anomalies.length > 0).length,
      all: all.length,
    }
  }, [mergedData])

  const isLoading = allClientsQuery.isLoading || aircraftQuery.isLoading || leaseQuery.isLoading || transientQuery.isLoading

  return {
    data: filteredData,
    allData: mergedData,
    isLoading,
    isError: allClientsQuery.isError,
    error: allClientsQuery.error,
    refetch: allClientsQuery.refetch,
    summary,
    viewCounts,
  }
}
